import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Typography,
  Box,
  Chip,
  Tooltip,
  IconButton,
  TextField,
  InputAdornment
} from '@mui/material'
import Alert from '@mui/material/Alert'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { PageLayout, PageHeader } from '../../components'
import { ResponsiveTable } from '../../components/TableComponents'
import { DataFilters, LoadingSpinner } from '../../components/atoms'
import { useUsageOverviewData } from '../../hooks'
import styles from './AdminUsageOverview.module.scss'

const STATUS_CONFIG = {
  'OVER 2X LIMIT': { color: 'var(--usage-color-critical-red)', bg: 'var(--usage-bg-critical-red)', label: 'Over 2x' },
  'OVER 1.5X LIMIT': { color: 'var(--usage-color-warning-orange)', bg: 'var(--usage-bg-warning-orange)', label: 'Over 1.5x' },
  'LIMIT EXCEEDED': { color: 'var(--usage-color-exceeded-red)', bg: 'var(--usage-bg-exceeded-red)', label: 'Exceeded' },
  CRITICAL: { color: 'var(--usage-color-critical-orange)', bg: 'var(--usage-bg-critical-orange)', label: 'Critical' },
  WARNING: { color: 'var(--usage-color-warning)', bg: 'var(--usage-bg-warning)', label: 'Warning' },
  NORMAL: { color: 'var(--usage-color-normal)', bg: 'var(--usage-bg-normal)', label: 'Normal' },
  'LOW USAGE': { color: 'var(--usage-color-low-blue)', bg: 'var(--usage-bg-low-blue)', label: 'Low Usage' },
  'CRITICAL LOW': { color: 'var(--usage-color-critical-low)', bg: 'var(--usage-bg-critical-low)', label: 'Critical Low' },
  'NO USAGE': { color: 'var(--usage-color-no-usage)', bg: 'var(--usage-bg-no-usage)', label: 'No Usage' }
}

const SUMMARY_CARDS = [
  { key: 'total', label: 'Total', color: 'var(--color-deep-blue)' },
  { key: 'overLimit', label: 'Over Limit', color: 'var(--usage-color-exceeded-red)' },
  { key: 'warning', label: 'Warning', color: 'var(--usage-color-warning)' },
  { key: 'normal', label: 'Normal', color: 'var(--usage-color-normal)' },
  { key: 'lowUsage', label: 'Low Usage', color: 'var(--usage-color-low-blue)' },
  { key: 'inactive', label: 'Inactive (3+ days)', color: 'var(--usage-color-critical-low)' }
]

const FILTER_OPTIONS = [
  { key: 'all', label: 'All' },
  { key: 'overLimit', label: 'Over Limit' },
  { key: 'warning', label: 'Warning (75%+)' },
  { key: 'lowUsage', label: 'Low Usage (25%-)' },
  { key: 'inactive', label: 'Inactive (3+ days)' }
]

function getUsageBarColor (percent) {
  if (percent >= 100) return 'var(--usage-color-exceeded-red)'
  if (percent >= 75) return 'var(--usage-color-warning)'
  if (percent <= 10) return 'var(--usage-color-critical-low)'
  if (percent <= 25) return 'var(--usage-color-low-blue)'
  return 'var(--usage-color-normal)'
}

function formatDate (dateStr) {
  if (!dateStr) return '-'
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  })
}

const getCampaignColumn = (navigate) => ({
  id: 'campaign_name',
  label: 'Campaign',
  sortable: true,
  renderCell: (row) => (
    <Typography
      className={styles.campaignName}
      variant='body2'
      style={{ cursor: 'pointer' }}
      onClick={() => navigate(`/campaign/${encodeURIComponent(row.campaign_name)}`)}
    >
      {row.campaign_name}
    </Typography>
  )
})

const STATIC_COLUMNS = [
  {
    id: 'plan_used_percent',
    label: 'Usage',
    sortable: true,
    align: 'center',
    style: { minWidth: 130 },
    renderCell: (row) => {
      const barColor = getUsageBarColor(row.plan_used_percent)
      const barWidth = Math.min(row.plan_used_percent, 100)
      return (
        <Box className={styles.usageBarContainer}>
          <Box className={styles.usageBar}>
            <Box
              className={styles.usageBarFill}
              style={{ width: `${barWidth}%`, backgroundColor: barColor }}
            />
          </Box>
          <Typography className={styles.usagePercent} style={{ color: barColor }}>
            {row.plan_used_percent}%
          </Typography>
        </Box>
      )
    }
  },
  {
    id: 'current_month_minutes',
    label: 'Current',
    sortable: true,
    align: 'right',
    renderCell: (row) => (
      <span className={styles.cellText}>{Math.round(row.current_month_minutes)} min</span>
    )
  },
  {
    id: 'plan_minutes',
    label: 'Plan',
    sortable: true,
    align: 'right',
    renderCell: (row) => (
      <span className={styles.cellText}>{row.plan_minutes} min</span>
    )
  },
  {
    id: 'call_count',
    label: 'Calls',
    sortable: true,
    align: 'right',
    renderCell: (row) => (
      <span className={styles.cellText}>{row.call_count}</span>
    )
  },
  {
    id: 'last_call',
    label: 'Last Call',
    sortable: true,
    align: 'center',
    renderCell: (row) => (
      <Tooltip title={row.last_call_date ? formatDate(row.last_call_date) : 'No calls recorded'}>
        <Box>
          <Typography className={styles.lastCallDate}>
            {formatDate(row.last_call_date)}
          </Typography>
          {row.days_since_last_call !== null && row.days_since_last_call >= 3 && (
            <Typography className={styles.inactiveDays}>
              {row.days_since_last_call}d inactive
            </Typography>
          )}
        </Box>
      </Tooltip>
    )
  },
  {
    id: 'status',
    label: 'Status',
    align: 'center',
    renderCell: (row) => {
      const statusConfig = STATUS_CONFIG[row.status] || STATUS_CONFIG.NORMAL
      return (
        <Chip
          label={statusConfig.label}
          size='small'
          className={styles.statusChip}
          style={{
            color: statusConfig.color,
            backgroundColor: statusConfig.bg,
            border: '1px solid',
            borderColor: statusConfig.bg
          }}
        />
      )
    }
  }
]

function exportUsageCsv (data) {
  const headers = ['Campaign', 'Usage %', 'Current Minutes', 'Plan Minutes', 'Calls', 'Last Call', 'Days Inactive', 'Status']
  const rows = data.map(row => [
    row.campaign_name,
    row.plan_used_percent,
    Math.round(row.current_month_minutes),
    row.plan_minutes,
    row.call_count,
    row.last_call_date ? formatDate(row.last_call_date) : '',
    row.days_since_last_call ?? '',
    row.status
  ])

  const csvContent = [headers, ...rows]
    .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    .join('\n')

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `usage-overview-${new Date().toISOString().slice(0, 10)}.csv`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

const AdminUsageOverview = () => {
  const navigate = useNavigate()
  const { campaigns, summary, loading, error, fetchUsageOverview } = useUsageOverviewData()
  const [filter, setFilter] = useState('all')
  const [sortField, setSortField] = useState('campaign_name')
  const [sortDirection, setSortDirection] = useState('asc')
  const [searchQuery, setSearchQuery] = useState('')

  const now = new Date()
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1)
  const [selectedYear, setSelectedYear] = useState(now.getFullYear())

  const COLUMNS = useMemo(() => [getCampaignColumn(navigate), ...STATIC_COLUMNS], [navigate])

  useEffect(() => {
    fetchUsageOverview(selectedMonth, selectedYear)
  }, [fetchUsageOverview, selectedMonth, selectedYear])

  const handleSort = useCallback((field) => {
    setSortDirection(prev => sortField === field && prev === 'asc' ? 'desc' : 'asc')
    setSortField(field)
  }, [sortField])

  const filteredCampaigns = useMemo(() => {
    let result = campaigns

    if (searchQuery.trim()) {
      const query = searchQuery.trim().toLowerCase()
      result = result.filter(c => c.campaign_name.toLowerCase().includes(query))
    }

    switch (filter) {
      case 'overLimit':
        result = result.filter(c => c.plan_used_percent >= 100)
        break
      case 'warning':
        result = result.filter(c => c.plan_used_percent >= 75 && c.plan_used_percent < 100)
        break
      case 'lowUsage':
        result = result.filter(c => c.plan_used_percent <= 25)
        break
      case 'inactive':
        result = result.filter(c => c.days_since_last_call !== null && c.days_since_last_call >= 3)
        break
      default:
        break
    }

    const actualField = sortField === 'last_call' ? 'days_since_last_call' : sortField

    return [...result].sort((a, b) => {
      let aVal = a[actualField]
      let bVal = b[actualField]

      if (aVal === null || aVal === undefined) aVal = sortDirection === 'asc' ? Infinity : -Infinity
      if (bVal === null || bVal === undefined) bVal = sortDirection === 'asc' ? Infinity : -Infinity

      if (typeof aVal === 'string') {
        return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal)
      }

      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal
    })
  }, [campaigns, filter, sortField, sortDirection, searchQuery])

  if (error) {
    return (
      <Alert severity='error' className={styles.errorAlert}>
        Error: {error}
      </Alert>
    )
  }

  const displayMonth = new Date(selectedYear, selectedMonth - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
  const isCurrentMonth = selectedMonth === now.getMonth() + 1 && selectedYear === now.getFullYear()

  const handlePreviousMonth = () => {
    if (selectedMonth === 1) {
      setSelectedMonth(12)
      setSelectedYear(selectedYear - 1)
    } else {
      setSelectedMonth(selectedMonth - 1)
    }
  }

  const handleNextMonth = () => {
    if (isCurrentMonth) return
    if (selectedMonth === 12) {
      setSelectedMonth(1)
      setSelectedYear(selectedYear + 1)
    } else {
      setSelectedMonth(selectedMonth + 1)
    }
  }

  return (
    <PageLayout>
      <PageHeader
        title='Usage Overview'
        subtitle='Monitor client plan usage and identify churn risks. Campaigns at 25% usage or below may indicate clients who have turned off call forwarding.'
      />

      {/* Month banner with navigation */}
      <Box className={styles.monthBanner}>
        <Box className={styles.monthBannerIcon}>
          <FontAwesomeIcon icon='calendar-alt' />
        </Box>
        <IconButton
          size='small'
          onClick={handlePreviousMonth}
          aria-label='Previous month'
          style={{ color: 'inherit' }}
        >
          <FontAwesomeIcon icon='chevron-left' style={{ fontSize: 12 }} />
        </IconButton>
        <Typography variant='body2' style={{ fontWeight: 500, minWidth: 160, textAlign: 'center' }}>
          Showing usage for <strong>{displayMonth}</strong>
        </Typography>
        <IconButton
          size='small'
          onClick={handleNextMonth}
          disabled={isCurrentMonth}
          aria-label='Next month'
          style={{ color: 'inherit' }}
        >
          <FontAwesomeIcon icon='chevron-right' style={{ fontSize: 12 }} />
        </IconButton>
      </Box>

      {/* Summary cards */}
      {summary && (
        <Box className={styles.summaryGrid}>
          {SUMMARY_CARDS.map(({ key, label, color }) => (
            <Box
              key={key}
              className={styles.summaryCard}
              onClick={() => setFilter(key === 'total' ? 'all' : key)}
              style={{ cursor: 'pointer', borderColor: filter === key || (key === 'total' && filter === 'all') ? color : undefined }}
            >
              <Typography className={styles.summaryValue} style={{ color }}>
                {summary[key] ?? 0}
              </Typography>
              <Typography className={styles.summaryLabel}>
                {label}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* Filter chips and search */}
      <DataFilters className={styles.filterSection}>
        <Typography className={styles.filterLabel}>Filter:</Typography>
        {FILTER_OPTIONS.map(({ key, label }) => (
          <Chip
            key={key}
            label={label}
            size='small'
            color={filter === key ? 'primary' : 'default'}
            onClick={() => setFilter(key)}
            variant={filter === key ? 'default' : 'outlined'}
          />
        ))}
        <Box style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
          <TextField
            size='small'
            placeholder='Search campaigns...'
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <FontAwesomeIcon icon='search' style={{ fontSize: 12, color: 'var(--color-text-secondary)' }} />
                </InputAdornment>
              )
            }}
            style={{ width: 200 }}
          />
          <Typography variant='body2' style={{ color: 'var(--color-text-secondary)' }}>
            {filteredCampaigns.length} campaign{filteredCampaigns.length !== 1 ? 's' : ''}
          </Typography>
          <Tooltip title='Export CSV'>
            <IconButton
              size='small'
              onClick={() => exportUsageCsv(filteredCampaigns)}
              disabled={filteredCampaigns.length === 0}
            >
              <FontAwesomeIcon icon='download' style={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </DataFilters>

      {/* Loading */}
      {loading && (
        <LoadingSpinner message='Loading usage data...' />
      )}

      {/* Table */}
      {!loading && (
        <ResponsiveTable
          columns={COLUMNS}
          data={filteredCampaigns}
          loading={false}
          searched={false}
          sortField={sortField}
          sortDirection={sortDirection}
          onSort={handleSort}
          onRowClick={(row) => navigate(`/campaign/${encodeURIComponent(row.campaign_name)}`)}
          variant='usageOverview'
          headerTitle='Usage'
          headerResultCount={filteredCampaigns.length}
          headerResultLabel='campaigns'
        />
      )}
    </PageLayout>
  )
}

export default AdminUsageOverview
